/**
 * CrisisStatusViews — SendingContent, SuccessContent, ErrorContent
 *
 * Extracted from CrisisPractitionerPrompt.tsx as part of E2 refactoring, S2.2 (#742).
 * These three components represent the loading, success, and error states of the
 * crisis practitioner request flow.
 */

import { View, Text, Pressable, ActivityIndicator } from 'react-native';
import { styles, type TFunction } from '../styles/crisis-practitioner.styles';

export interface SendingContentProps {
  t: TFunction;
}

export interface SuccessContentProps {
  t: TFunction;
  onDone: () => void;
}

export interface ErrorContentProps {
  t: TFunction;
  errorMessage: string;
  onRetry: () => void;
  onClose: () => void;
}

export function SendingContent({ t }: SendingContentProps) {
  return (
    <View style={styles.contentContainer}>
      <View style={styles.centerContent}>
        <ActivityIndicator size="large" color="#6366F1" />
        <Text style={styles.sendingText}>
          {t('practitioner_selector.sending')}
        </Text>
      </View>
    </View>
  );
}

export function SuccessContent({ t, onDone }: SuccessContentProps) {
  return (
    <View style={styles.contentContainer}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerIcon}>✅</Text>
        <Text style={styles.headerTitle}>{t('request_success.title')}</Text>
      </View>

      {/* Description */}
      <View style={styles.descriptionSection}>
        <Text style={styles.description}>
          {t('request_success.description')}
        </Text>
        <View style={styles.reminderNotice}>
          <Text style={styles.reminderNoticeText}>
            {t('request_success.reminder')}
          </Text>
        </View>
      </View>

      {/* Done Button */}
      <View style={styles.buttonContainer}>
        <Pressable
          onPress={onDone}
          style={styles.primaryButton}
          accessibilityRole="button"
          testID="crisis-practitioner-done"
        >
          <Text style={styles.primaryButtonText}>
            {t('request_success.done')}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

export function ErrorContent({
  t,
  errorMessage,
  onRetry,
  onClose,
}: ErrorContentProps) {
  return (
    <View style={styles.contentContainer}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerIcon}>❌</Text>
        <Text style={styles.headerTitle}>{t('request_error.title')}</Text>
      </View>

      {/* Description */}
      <View style={styles.descriptionSection}>
        <Text style={styles.description}>{t('request_error.description')}</Text>
        {errorMessage && (
          <Text style={styles.errorDetail}>{errorMessage}</Text>
        )}
      </View>

      {/* Action Buttons */}
      <View style={styles.buttonContainer}>
        <Pressable
          onPress={onRetry}
          style={styles.primaryButton}
          accessibilityRole="button"
          testID="crisis-practitioner-retry"
        >
          <Text style={styles.primaryButtonText}>
            {t('request_error.retry')}
          </Text>
        </Pressable>

        <Pressable
          onPress={onClose}
          style={styles.secondaryButton}
          accessibilityRole="button"
          testID="crisis-practitioner-close-error"
        >
          <Text style={styles.secondaryButtonText}>
            {t('request_error.contact_directly')}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}
